import { SharedCareQuestionnaireItem } from 'components/Assessments/hooks/constants'
import { CustomFeatureConfigKey } from 'utils/featureConfig/constants'
import { checkFeatureConfigByServiceTypes } from 'utils/featureConfig'
import { AssessmentStatus } from 'fhir/QuestionnaireResponse/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { mapAssessmentADLItemIntoADLNeeds } from './mapper'
import { ADLNeed, ADLNeedTemplate } from './types'

export const getAdlNeeds = ({
  patSunset,
  flatAnswers,
  adlNeeds,
  serviceTypes,
  assessmentStatus
}: {
  patSunset: boolean
  flatAnswers: any
  adlNeeds?: ADLNeedTemplate[]
  serviceTypes?: ServiceTypeName[]
  assessmentStatus?: string
}): {
  formattedADLNeeds: ADLNeed[]
  shouldRenderSkilledNeeds: boolean
  shouldRenderADLNeeds: boolean
} => {
  const rawADLNeedsAnswerList = flatAnswers[SharedCareQuestionnaireItem('ADL_NEEDS_NEED')]
  const adlNeedsAnswerList = rawADLNeedsAnswerList?.answer ?? []

  const isSkilledNeedsAllowed = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.SkilledNeeds,
    serviceTypes ?? undefined
  )

  const isAdlNeedsAllowed = checkFeatureConfigByServiceTypes(
    CustomFeatureConfigKey.ADLNeeds,
    serviceTypes ?? undefined
  )

  const isAssessmentEditable = assessmentStatus === AssessmentStatus.IN_PROGRESS

  const adlNeedsSectionExists = adlNeedsAnswerList.length > 0

  /*
   * For backward compatibility, we render the skilled needs section if:
   * - The PAT sunset feature flag is not enabled
   * - OR the PAT sunset feature flag is enabled but the ADL needs section does not have any items
   * This ensures that existing assessments with skilled needs can still be viewed.
   */
  const renderSkilledNeedsForBackwardCompatibility =
    patSunset && !adlNeedsSectionExists && !isAssessmentEditable

  const shouldRenderSkilledNeeds =
    isSkilledNeedsAllowed && (!patSunset || renderSkilledNeedsForBackwardCompatibility)

  /*   * For ADL needs, we render the section if:
   * - The PAT sunset feature flag is enabled and ADL needs are allowed
   * - OR the PAT sunset feature flag is enabled, ADL needs are allowed, and the section exists with answers
   * - OR the assessment is editable so that users can add or edit ADL needs
   * This ensures that ADL needs can be edited on new assessments or to render if the section exists with answers.
   */
  const shouldRenderADLNeeds =
    patSunset && isAdlNeedsAllowed && (adlNeedsSectionExists || isAssessmentEditable)

  return {
    formattedADLNeeds: mapAssessmentADLItemIntoADLNeeds(adlNeedsAnswerList, adlNeeds),
    shouldRenderSkilledNeeds,
    shouldRenderADLNeeds
  }
}
